import { Skeleton } from '@/components/ui';
import { AnswerFeedback, AnswerView, ErrorState } from '@/components/page';
import type { AskStreamAccum } from '@/lib/api/ask-stream';
import { imageDataUrl, type ChatImage } from '@/components/chat';

/**
 * One message of the Shiba conversation. User turns carry only `question`;
 * assistant turns hold the async answer state (see {@link AskStreamAccum} for
 * the shared fields), with `progress` carrying the run's friendly stage line
 * while it cooks and `error` marking a failed, retryable turn.
 */
export interface ShibaMessage extends Omit<AskStreamAccum, 'status'> {
  id: string;
  role: 'user' | 'assistant';
  /** The user text (role 'user') or the question being answered (role 'assistant', for retry). */
  question: string;
  /** Image the question was asked with: shown on the user turn, resent on retry. */
  image?: ChatImage;
  status: AskStreamAccum['status'] | 'error';
  /** Friendly pt-BR progress line while the background run is `running`. */
  progress?: string;
  error?: unknown;
}

export interface ShibaMessageViewProps {
  message: ShibaMessage;
  /** Re-ask the same question (used from the error state). */
  onRetry: (message: ShibaMessage) => void;
}

/**
 * A single Shiba turn: user bubble right-aligned, assistant bubble left —
 * skeleton plus the run's friendly progress line while the background job
 * cooks, the {@link AnswerView} once the answer lands, {@link AnswerFeedback}
 * once it is persisted, or a retryable {@link ErrorState} on failure. Messages
 * that were never persisted (the welcome note) render as a plain bubble,
 * without the provenance chrome.
 */
export function ShibaMessageView({ message, onRetry }: ShibaMessageViewProps): JSX.Element {
  if (message.role === 'user') {
    return (
      <div className="shiba-msg shiba-msg--user">
        {message.image && (
          <img
            className="shiba-msg__image"
            src={imageDataUrl(message.image)}
            alt={`Imagem enviada: ${message.image.name}`}
          />
        )}
        <div className="shiba-msg__bubble">{message.question}</div>
      </div>
    );
  }

  const plain =
    message.status === 'done' && !message.interactionId && message.sources.length === 0;

  return (
    <div className="shiba-msg shiba-msg--assistant">
      {message.status === 'pending' && (
        <div className="shiba-msg__pending" role="status" aria-label="Shiba está gerando resposta">
          {message.progress && <p className="shiba-msg__progress">{message.progress}</p>}
          <Skeleton width="90%" height="0.9rem" />
          <Skeleton width="70%" height="0.9rem" />
        </div>
      )}

      {message.status === 'error' && (
        <ErrorState
          error={message.error}
          title="Falha ao consultar a Shiba"
          onRetry={() => onRetry(message)}
        />
      )}

      {(message.status === 'streaming' || message.status === 'done') &&
        (plain ? (
          <div className="shiba-msg__bubble">{message.answer}</div>
        ) : (
          <>
            <AnswerView
              answer={message.answer}
              sources={message.sources}
              confidence={message.confidence}
            />
            {message.status === 'done' && message.interactionId && (
              <AnswerFeedback interactionId={message.interactionId} />
            )}
          </>
        ))}
    </div>
  );
}
